import { SelectOption } from '@/constants';
import { Country } from '@/utils/cloud-utils';

export class BaseSpaceUtils {
  /**
   * Validates the BaseSpace region prefix.
   * Throws an error if the prefix is invalid.
   * @param regionPrefix The BaseSpace region prefix to validate.
   */
  private static validateRegionPrefix(regionPrefix: string): void {
    // throw error if region not '' or 'euc1.sh.'
    if (regionPrefix !== '' && regionPrefix !== 'euc1.sh.') {
      throw new Error(`Invalid BaseSpace region prefix: ${regionPrefix}`);
    }
  }

  /**
   * Returns the BaseSpace OAuth redirect URI.
   * Throws an error if the redirect URI is not set in environment variables.
   * @returns The BaseSpace redirect URI.
   */
  public static redirectUri(): string {
    const redirectUri = process.env.VUE_APP_BASESPACE_REDIRECT_URL || '';
    if (!redirectUri) {
      throw new Error(
        'BaseSpace redirect URI is not set in environment variables.'
      );
    }
    return redirectUri;
  }

  /**
   * Returns the BaseSpace client ID based on the region prefix.
   * Throws an error if the client ID is not set for the given region.
   * @param regionPrefix The BaseSpace region prefix.
   * @returns The BaseSpace client ID.
   */
  public static basespaceClientId(regionPrefix: string): string {
    let clientId: string | null = null;
    this.validateRegionPrefix(regionPrefix);

    // determine client id based on region prefix
    if (regionPrefix === 'euc1.sh.') {
      clientId = process.env.VUE_APP_BASESPACE_EU_CLIENT_ID || null;
    } else {
      clientId = process.env.VUE_APP_BASESPACE_US_CLIENT_ID || null;
    }

    // check client id is not null
    if (!clientId) {
      throw new Error(
        `BaseSpace client ID not set for region prefix: ${regionPrefix}`
      );
    }
    return clientId;
  }

  /**
   * Returns the BaseSpace OAuth authorization URL based on the region prefix.
   * Throws an error if the region prefix is invalid.
   * @param regionPrefix The BaseSpace region prefix.
   * @return The BaseSpace OAuth authorization URL.
   * */
  public static basespaceAuthUrl(regionPrefix: string): string {
    this.validateRegionPrefix(regionPrefix);
    return 'https://' + regionPrefix + 'basespace.illumina.com/oauth/authorize';
  }
}

export interface BasespaceRegion<
  P extends string = string,
  C extends Country = Country
> {
  readonly regionPrefix: P; // e.g. '' | 'euc1.sh.' | null
  readonly label: string; // human-readable
  readonly countries: readonly C[];
}

export const BASESPACE_REGIONS = [
  // ––––– real regions –––––
  {
    regionPrefix: 'euc1.sh.',
    label: 'Europe (euc1)',
    countries: [
      { code: 'AT', name: 'Austria' },
      { code: 'CZ', name: 'Czech Republic' },
      { code: 'DE', name: 'Germany' },
      { code: 'CH', name: 'Switzerland' },
    ] as const,
  },
  {
    regionPrefix: '',
    label: 'United States',
    countries: [
      { code: 'US', name: 'United States' },
      { code: 'CA', name: 'Canada' },
    ] as const,
  },
] as const satisfies readonly BasespaceRegion[];

export type BasespaceServerPrefix =
  (typeof BASESPACE_REGIONS)[number]['regionPrefix'];

export const BasespaceRegionPrefixSelectOptions: readonly SelectOption<BasespaceServerPrefix>[] =
  [
    ...BASESPACE_REGIONS.filter((r) => r.regionPrefix !== null).map((r) => ({
      text: r.label,
      value: r.regionPrefix,
    })), // typed automatically
  ] as const;
